import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import { HttpErrorResponse } from '@angular/common/http';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import { AdminFee, AdminFeesService } from '../../../services/admin-fees/admin-fees.service';
import { Partner, PartnersService } from '../../../services/partners/partners.service';
import { VendorProduct, VendorProductsService } from '../../../services/vendorproducts/vendorproducts.service';
import {
  AdminFeeEditorDialogComponent,
  AdminFeeEditorDialogResult,
  BrandShoppingCartConfigUpdate
} from './admin-fee-editor-dialog/admin-fee-editor-dialog.component';

@Component({
    selector: 'app-admin-fees-page',
    templateUrl: './admin-fees-page.component.html',
    styleUrls: ['./admin-fees-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AdminFeesPageComponent implements OnInit {
  @ViewChild(MatSort) sort!: MatSort;

  protected allColumnNames = ['name', 'amountCents', 'scope', 'cartLabel', 'actionmenu'];
  protected items = new MatTableDataSource<any>();
  protected isEditorDataReady = false;
  protected partners: Partner[] = [];
  protected vendorProducts: VendorProduct[] = [];

  private loadedPartners = false;
  private loadedVendorProducts = false;

  constructor(
    private adminFeesService: AdminFeesService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private spinnerService: SpinnerService,
    private uiAlertService: UiAlertService,
    private partnersService: PartnersService,
    private vendorProductsService: VendorProductsService,
  ) {}

  ngOnInit(): void {
    this.reloadItems();
  }

  addItem(): void {
    const dialogRef = this.openEditorDialog('create');
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.persistAdminFeeAndBrandConfig(
          () => this.adminFeesService.createAdminFee(this.toAdminFeePayload(dialogResult)),
          dialogResult,
          'Admin fee created',
          'Unable to create admin fee'
        );
      }
    });
  }

  deleteItem(item: any): void {
    this.uiAlertService.presentAlertConfirm('Are you sure you want to delete this admin fee?').then(async confirm => {
      if (confirm) {
        this.spinnerService.show('Deleting admin fee...');
        this.adminFeesService.deleteAdminFee(item.id).then(() => {
          this.reloadItems();
          this.spinnerService.hide();
          this.snackBar.open('Admin fee deleted', 'Close', {
            duration: 3000
          });
        }).catch((err: HttpErrorResponse) => {
          this.spinnerService.hide();
          this.snackBar.open('Unable to delete admin fee', 'Close', {
            duration: 3000
          });
        });
      }
    });
  }

  editItem(item: any): void {
    const dialogRef = this.openEditorDialog('edit', item as AdminFee);
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.persistAdminFeeAndBrandConfig(
          () => this.adminFeesService.updateAdminFee(item.id, this.toAdminFeePayload(dialogResult)),
          dialogResult,
          'Admin fee updated',
          'Unable to update admin fee'
        );
      }
    });
  }

  duplicateItem(item: any): void {
    const sourceAdminFee = this.toPlainAdminFee(item as AdminFee);
    const duplicatedAdminFee: AdminFee = {
      ...sourceAdminFee,
      id: '',
      name: null,
      vendorId: '',
    };

    const dialogRef = this.openEditorDialog('create', duplicatedAdminFee);
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.persistAdminFeeAndBrandConfig(
          () => this.adminFeesService.createAdminFee(this.toAdminFeePayload(dialogResult)),
          dialogResult,
          'Admin fee duplicated',
          'Unable to duplicate admin fee'
        );
      }
    });
  }

  private reloadItems(): void {
    this.loadedPartners = false;
    this.loadedVendorProducts = false;
    this.isEditorDataReady = false;

    this.adminFeesService.getAllAdminFees().then(adminFees => {
      const normalizedAdminFees = Array.isArray(adminFees)
        ? adminFees.map((adminFee) => this.toPlainAdminFee(adminFee))
        : [];
      // Default to alphabetical sort
      const sortAdminFees = (adminFee1: AdminFee, adminFee2: AdminFee) => {
        if (adminFee1?.name && adminFee2?.name) {
          return adminFee1.name > adminFee2.name ? 1 : -1;
        }
        return 0;
      };
      this.items.data = normalizedAdminFees.sort(sortAdminFees).map(adminFee => {
        adminFee.always = adminFee?.appliesWhen?.always;
        adminFee.stripeProductId = adminFee?.appliesWhen?.stripeProductId;
        adminFee.vendorProductId = adminFee?.appliesWhen?.vendorProductId;
        adminFee.cartLabel = adminFee?.appliesWhen?.cartLabel || adminFee?.appliesWhen?.feeName;
        adminFee.moreInfoHtml = adminFee?.appliesWhen?.moreInfoHtml || adminFee?.appliesWhen?.moreInfo;
        adminFee.chargeMode = adminFee?.appliesWhen?.chargeMode
          || (adminFee?.appliesWhen?.stripeProductId ? 'line_item' : (adminFee?.always ? 'line_item' : 'bundled'));
        adminFee.amountRules = adminFee?.appliesWhen?.amountRules;
        return adminFee;
      });
      this.items.sort = this.sort;
    }).catch(() => {
      this.items.data = [];
      this.snackBar.open('Unable to load admin fees', 'Close', {
        duration: 4000
      });
    });
    this.partnersService.getAllPartners().then(partners => {
      this.partners = partners;
      this.loadedPartners = true;
      this.updateEditorDataReadyState();
    });
    this.vendorProductsService.getAllVendorProducts().then((result: Array<VendorProduct>) => {
      this.vendorProducts = result;
      this.loadedVendorProducts = true;
      this.updateEditorDataReadyState();
    });
  }

  protected getScope(item: AdminFee): string {
    if (item?.always) {
      return 'All products';
    }
    if (item?.vendorProductId) {
      const vendorProduct = this.vendorProducts.find((product) => product.id === item.vendorProductId);
      return vendorProduct ? `Vendor product: ${vendorProduct.productName}` : `Vendor product: ${item.vendorProductId}`;
    }
    return 'Not configured';
  }

  protected getCartLabel(item: AdminFee): string {
    return item?.cartLabel || 'Admin fee';
  }

  private openEditorDialog(mode: 'create' | 'edit', adminFee?: AdminFee) {
    return this.dialog.open<AdminFeeEditorDialogComponent, any, AdminFeeEditorDialogResult | null>(
      AdminFeeEditorDialogComponent,
      {
        width: '980px',
        maxHeight: '90vh',
        data: {
          mode,
          partners: this.partners,
          vendorProducts: this.vendorProducts,
          adminFee: adminFee ? this.toPlainAdminFee(adminFee) : null,
        },
      }
    );
  }

  private persistAdminFeeAndBrandConfig(
    persistAdminFee: () => Promise<any>,
    dialogResult: AdminFeeEditorDialogResult,
    successMessage: string,
    failureMessage: string,
  ): void {
    persistAdminFee().then(() => {
      const brandConfigUpdate = dialogResult.brandConfigUpdate;
      if (!brandConfigUpdate) {
        this.reloadItems();
        this.snackBar.open(successMessage, 'Close', {
          duration: 3000
        });
        return;
      }

      this.persistBrandShoppingCartConfig(brandConfigUpdate).then(() => {
        this.reloadItems();
        this.snackBar.open(`${successMessage} and shopping cart settings updated`, 'Close', {
          duration: 3500
        });
      }).catch(() => {
        this.reloadItems();
        this.snackBar.open(`${successMessage}, but unable to update shopping cart settings`, 'Close', {
          duration: 5000
        });
      });
    }).catch(() => {
      this.snackBar.open(failureMessage, 'Close', {
        duration: 4000
      });
    });
  }

  private persistBrandShoppingCartConfig(configUpdate: BrandShoppingCartConfigUpdate): Promise<void> {
    const matchingPartner = this.partners.find((partner) => String(partner.partnerId || '') === String(configUpdate.partnerId));
    if (!matchingPartner) {
      return Promise.reject(new Error('Unable to find selected brand'));
    }

    const partnerPayload = this.toPlainPartner(matchingPartner);
    partnerPayload.configjson = configUpdate.configjson;
    return this.partnersService.updatePartner(partnerPayload).then(() => undefined);
  }

  private toAdminFeePayload(dialogResult: AdminFeeEditorDialogResult): Record<string, any> {
    return {
      vendorId: dialogResult.vendorId,
      amountCents: dialogResult.amountCents,
      appliesWhen: dialogResult.appliesWhen,
    };
  }

  private updateEditorDataReadyState(): void {
    this.isEditorDataReady = this.loadedPartners && this.loadedVendorProducts;
  }

  private toPlainPartner(partner: Partner): Partner {
    try {
      return JSON.parse(JSON.stringify(partner));
    } catch (_) {
      return {
        ...partner,
      };
    }
  }

  private toPlainAdminFee(adminFee: AdminFee): AdminFee {
    const appliesWhen = this.toPlainAppliesWhen(adminFee?.appliesWhen);
    const parsedAmountCents = Number(adminFee?.amountCents);
    const amountCents = Number.isFinite(parsedAmountCents) ? parsedAmountCents : 0;
    return {
      id: String(adminFee?.id || ''),
      vendorId: String(adminFee?.vendorId || 'ALL'),
      name: adminFee?.name || null,
      amountCents,
      appliesWhen,
      always: appliesWhen?.always,
      stripeProductId: appliesWhen?.stripeProductId,
      vendorProductId: appliesWhen?.vendorProductId,
      cartLabel: appliesWhen?.cartLabel || appliesWhen?.feeName,
      moreInfoHtml: appliesWhen?.moreInfoHtml || appliesWhen?.moreInfo,
      chargeMode: appliesWhen?.chargeMode
        || (appliesWhen?.stripeProductId ? 'line_item' : (appliesWhen?.always ? 'line_item' : 'bundled')),
      amountRules: appliesWhen?.amountRules,
    };
  }

  private toPlainAppliesWhen(appliesWhen: unknown): Record<string, any> {
    if (!appliesWhen || typeof appliesWhen !== 'object') {
      return {};
    }
    try {
      return JSON.parse(JSON.stringify(appliesWhen));
    } catch (error) {
      return {};
    }
  }
}
